import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": "https://www.saskroofingcompany.com/contact#webpage",
  url: "https://www.saskroofingcompany.com/contact",
  name: "Contact Sask Roofing Company | Free Roof Inspection",
  isPartOf: { "@id": "https://www.saskroofingcompany.com/#website" },
  inLanguage: "en-CA",
  about: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
  mainEntity: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
};

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      await fetch(
        "https://services.leadconnectorhq.com/hooks/2uky3JASjEkhr90sRnhQ/webhook-trigger/0720f8f9-5be8-467e-8fbb-c723f74765a7",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
    } catch (error) {
      console.error("Error submitting form:", error);
    }

    await new Promise((resolve) => setTimeout(resolve, 800));
    setIsSubmitting(false);
    setIsSubmitted(true);
    e.currentTarget.reset();

    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Contact Sask Roofing Company | Free Roof Inspection"
        description="Need a free roof inspection or emergency repair? Reach out to Sask Roofing Company today. We serve homeowners across Saskatchewan."
        canonical="/contact"
        schemaJson={[contactPageSchema]}
      />
      <section className="bg-muted/30 py-16 md:py-24 border-b">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
              Contact Us
            </h1>
            <p className="text-lg text-muted-foreground">
              Need a free roof inspection or emergency repair? Reach out today.
              We serve homeowners across Saskatchewan.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="space-y-8 bg-background p-8 rounded-xl border shadow-sm">
              <h2 className="text-2xl font-bold">Request a Free Inspection</h2>
              <p className="text-muted-foreground">
                Fill out the form below and our team will get back to you within
                24 hours to schedule your inspection.
              </p>

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      placeholder="John"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(639) 739-7969"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      placeholder="e.g. Saskatoon"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service">Service Needed</Label>
                    <select
                      id="service"
                      name="service"
                      required
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a service...</option>
                      <option value="Free Roof Inspection">
                        Free Roof Inspection
                      </option>
                      <option value="Roof Replacement">Roof Replacement</option>
                      <option value="Roof Repair">Roof Repair</option>
                      <option value="Storm / Hail Damage">
                        Storm / Hail Damage
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us a bit about your roof..."
                    className="min-h-[120px]"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 text-lg py-6 h-auto transition-all"
                >
                  {isSubmitting
                    ? "Sending..."
                    : isSubmitted
                      ? "Request Submitted!"
                      : "Submit Request"}
                </Button>
                {isSubmitted && (
                  <p className="text-sm text-green-600 text-center font-medium mt-2">
                    Thank you! We will get back to you within 24 hours.
                  </p>
                )}
              </form>
            </div>

            <div className="space-y-8">
              <div className="bg-primary text-primary-foreground p-8 rounded-xl space-y-6">
                <h3 className="text-xl font-bold">Contact Information</h3>
                <div className="space-y-4">
                  <a
                    href="tel:6397397969"
                    className="flex items-center gap-3 hover:text-accent transition-colors"
                  >
                    <Phone className="w-6 h-6 shrink-0" />
                    <span className="text-lg font-bold">(639) 739-7969</span>
                  </a>
                  <a
                    href="mailto:info@saskroofingcompany.com"
                    className="flex items-center gap-3 hover:text-accent transition-colors"
                  >
                    <Mail className="w-6 h-6 shrink-0" />
                    <span className="text-lg">info@saskroofingcompany.com</span>
                  </a>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-6 h-6 shrink-0 mt-1" />
                    <span className="text-lg">
                      Proudly serving Saskatoon, Regina, Moose Jaw, and
                      communities across Saskatchewan.
                    </span>
                  </div>
                  <div className="flex items-start gap-3 pt-4 border-t border-primary-foreground/20">
                    <Clock className="w-6 h-6 shrink-0 mt-1" />
                    <div>
                      <p className="font-bold mb-1">Business Hours</p>
                      <p className="text-primary-foreground/80">
                        Monday - Friday: 8:00 AM - 6:00 PM
                      </p>
                      <p className="text-primary-foreground/80">
                        Saturday: 9:00 AM - 2:00 PM
                      </p>
                      <p className="text-primary-foreground/80">
                        Sunday: Closed
                      </p>
                      <p className="text-sm mt-2 text-accent font-medium">
                        24/7 Emergency Service Available
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-muted p-8 rounded-xl border">
                <h3 className="text-xl font-bold mb-4">
                  Service Area Reminder
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  We are a dedicated service-area business proudly serving
                  homeowners across Saskatchewan. We bring our expertise
                  directly to your property.
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm font-medium text-foreground">
                  <span>• Saskatoon</span>
                  <span>• Regina</span>
                  <span>• Moose Jaw</span>
                  <span>• Prince Albert</span>
                  <span>• Swift Current</span>
                  <span>• Yorkton</span>
                  <span>• Estevan</span>
                  <span>• Weyburn</span>
                </div>
                <p className="text-xs text-muted-foreground mt-4 italic">
                  ...and surrounding Saskatchewan communities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Callout Grid */}
      <section className="py-12 bg-background border-b">
        <div className="container px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">24h</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Response Time
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">Free</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Inspection Always
              </span>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-muted/30 rounded-xl border">
              <span className="text-3xl font-bold text-primary mb-2">24/7</span>
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Emergency Line
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">
                Why Homeowners Call Sask Roofing
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                When you notice a leak, missing shingles, or storm damage, you
                need answers quickly. That's why we offer a comprehensive free
                inspection with absolutely no obligation. We provide a same-day
                diagnosis so you aren't left wondering about the state of your
                roof.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Every inspection comes with a detailed, written quote. No hidden
                fees, no high-pressure sales tactics. If you have a true
                emergency, our 24/7 line ensures we can dispatch a crew to tarp
                your roof and prevent further interior damage immediately. Need
                help managing the cost? We offer{" "}
                <Link
                  to="/financing"
                  className="text-primary hover:underline font-medium"
                >
                  flexible financing options
                </Link>{" "}
                to fit your budget.
              </p>
            </div>
            <div className="space-y-4 bg-muted/30 p-6 rounded-xl border">
              <h3 className="text-2xl font-bold">
                What to Have Ready When You Call
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                To help us serve you faster, it's helpful if you have a few
                details ready when you reach out:
              </p>
              <ul className="space-y-3 text-muted-foreground text-sm">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <span>
                    <strong>Roof age:</strong> An approximate age helps us
                    determine if repair or replacement is more likely.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <span>
                    <strong>Specific concerns:</strong> Are there active leaks,
                    missing shingles, or ice damming?
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <span>
                    <strong>Photos:</strong> If you have safely taken photos of
                    the damage, they are great for our initial assessment.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                  <span>
                    <strong>Insurance:</strong> If you suspect storm or hail
                    damage, having your policy details handy speeds up the
                    process.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-primary text-primary-foreground text-center">
        <div className="container px-4">
          <h2 className="text-3xl font-bold mb-6">Prefer to call?</h2>
          <Button
            size="lg"
            className="bg-accent text-accent-foreground hover:bg-accent/90 text-xl px-8 py-6 h-auto"
            asChild
          >
            <a href="tel:6397397969">(639) 739-7969</a>
          </Button>
        </div>
      </section>
    </div>
  );
}
